/**
 * TransformControls, wired through a proxy object rather than straight onto the thing being
 * edited.
 *
 * Attaching the gizmo directly to a bone would let three write `position` and `scale` onto it,
 * and would make "what the user dragged" and "what we store" the same object — with an imported
 * rig whose bones have arbitrary local axes, that is how translation sneaks into a rotation-only
 * format. Instead the gizmo drives an empty `Object3D` in the overlay group and we decide, per
 * mode, exactly which scalar values that turns into:
 *
 *  - ANIMATE → rotate/local on the selected bone; writes one canonical quaternion per change.
 *  - RIG     → translate/world on the selected joint; writes a placement position.
 *  - MAP / MODEL / LIBRARY → detached. Those modes have nothing to transform.
 *
 * A single TransformControls instance is shared with the IK handles (two live instances fight
 * over the same pointer events), so ownership goes through `setClaim`.
 */

import { Object3D, Quaternion } from 'three';
import type { Camera, Group, Scene } from 'three';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import type { OrbitControls } from 'three/addons/controls/OrbitControls.js';

import { CANONICAL_BONE_IDS } from '../../shared';
import type { CanonicalBoneId } from '../../shared';
import type { EditorContext } from '../context';
import { syncProxyToBone, writeProxyRotationToBone } from './gizmoMath';

export type GizmoMode = 'translate' | 'rotate';

/** Which handles a claim offers. Omitted (the normal case) means all three. */
export interface GizmoAxes {
  x: boolean;
  y: boolean;
  z: boolean;
}

export interface GizmoClaim {
  /** Identity of the claim; a claim with an unchanged key is not re-attached. */
  key: string;
  object: Object3D;
  mode: GizmoMode;
  space: 'local' | 'world';
  /**
   * Restrict the gizmo to some of its axes. The hips-height handle is the reason this exists: it
   * is a translate claim that may only move along the model's up vector, and offering X/Z would
   * let a drag author horizontal travel the clip format cannot represent — "Root belongs to the
   * game, Hips belongs to the clip". A claim without this gets all three back, so the shared
   * controls never leak one claim's restriction into the next.
   */
  axes?: GizmoAxes;
  onChange(): void;
  onDragStart(): void;
  onDragEnd(): void;
}

export interface GizmoService {
  controls: TransformControls;
  /** True while a drag gesture is in progress — pointer handlers must stand down. */
  isDragging(): boolean;
  /** True while the pointer is over a gizmo axis, i.e. a click here belongs to the gizmo. */
  isHot(): boolean;
  setClaim(claim: GizmoClaim | null): void;
  currentKey(): string | null;
  dispose(): void;
}

export function createGizmoService(deps: {
  camera: Camera;
  domElement: HTMLElement;
  scene: Scene;
  orbit: OrbitControls;
  requestRender(): void;
}): GizmoService {
  const controls = new TransformControls(deps.camera, deps.domElement);
  controls.setSize(0.8);
  const helper = controls.getHelper();
  helper.userData.editorOnly = true;
  deps.scene.add(helper);
  controls.detach();

  let claim: GizmoClaim | null = null;
  let dragging = false;

  const onDraggingChanged = (event: { value: unknown }): void => {
    const active = Boolean(event.value);
    dragging = active;
    // Orbiting while dragging a gizmo makes the drag unusable; hand the pointer to one or the
    // other, never both.
    deps.orbit.enabled = !active;
    if (!claim) return;
    if (active) claim.onDragStart();
    else claim.onDragEnd();
    deps.requestRender();
  };

  const onObjectChange = (): void => {
    claim?.onChange();
    deps.requestRender();
  };

  controls.addEventListener('dragging-changed', onDraggingChanged);
  controls.addEventListener('objectChange', onObjectChange);

  return {
    controls,
    isDragging: () => dragging,
    isHot: () => dragging || controls.axis !== null,
    currentKey: () => claim?.key ?? null,
    setClaim(next) {
      // Never swap the gizmo out from under an in-flight drag.
      if (dragging) return;
      if (next && claim && next.key === claim.key && next.object === claim.object) {
        claim = next;
        return;
      }
      claim = next;
      if (!next) {
        controls.detach();
        return;
      }
      controls.setMode(next.mode);
      controls.setSpace(next.space);
      // Written on EVERY claim, not only on the restricted ones: one controls instance is shared,
      // so a claim that says nothing about axes has to actively get all three back.
      const axes = next.axes;
      controls.showX = axes ? axes.x : true;
      controls.showY = axes ? axes.y : true;
      controls.showZ = axes ? axes.z : true;
      controls.attach(next.object);
    },
    dispose() {
      controls.removeEventListener('dragging-changed', onDraggingChanged);
      controls.removeEventListener('objectChange', onObjectChange);
      controls.detach();
      deps.scene.remove(helper);
      helper.dispose();
      controls.dispose();
      deps.orbit.enabled = true;
    },
  };
}

// ---------------------------------------------------------------------------
// Bone / joint claim
// ---------------------------------------------------------------------------

export interface BoneGizmo {
  /** Claim for the current mode + selection, or null when nothing is transformable. */
  claim(): GizmoClaim | null;
  /** Re-place the proxy from the model (skipped while dragging, or the drag would fight it). */
  sync(dragging: boolean): void;
  /** ANIMATE only: swap between rotate (`R`) and — never — translate. Returns the active mode. */
  setAnimateMode(mode: 'rotate'): void;
  dispose(): void;
}

const _proxyWorld = new Quaternion();

export function createBoneGizmo(overlayGroup: Group, ctx: EditorContext): BoneGizmo {
  const proxy = new Object3D();
  proxy.name = 'asx-gizmo-proxy';
  proxy.userData.editorOnly = true;
  overlayGroup.add(proxy);

  /** Bone the proxy currently represents, so `onChange` cannot write to a stale selection. */
  let boundBone: CanonicalBoneId | null = null;
  let boundJoint: CanonicalBoneId | null = null;

  function onRotateChange(): void {
    const rig = ctx.session.rig;
    if (!rig || !boundBone) return;
    const bone = rig.boneObject(boundBone);
    if (!bone) return;

    proxy.getWorldQuaternion(_proxyWorld);
    writeProxyRotationToBone(bone, _proxyWorld);
    // Read straight back out in canonical space — the store never sees source-local rotations.
    ctx.store.actions.setLivePoseBone(boundBone, rig.readCanonicalLocal(boundBone));
  }

  function onTranslateChange(): void {
    if (!boundJoint) return;
    ctx.store.actions.moveJoint(boundJoint, [proxy.position.x, proxy.position.y, proxy.position.z]);
  }

  function claim(): GizmoClaim | null {
    const state = ctx.store.getState();

    if (state.mode === 'animate') {
      const bone = state.selection.bone;
      const rig = ctx.session.rig;
      if (!bone || !rig || !rig.boneObject(bone)) return null;
      boundBone = bone;
      boundJoint = null;
      return {
        key: `animate:${bone}`,
        object: proxy,
        // Rotation only, always. Translate/scale in ANIMATE would author data the clip format
        // cannot represent (SPEC "Animation-mode transforms").
        mode: 'rotate',
        space: 'local',
        onChange: onRotateChange,
        onDragStart: () => ctx.store.actions.beginPoseDrag(bone),
        onDragEnd: () => ctx.store.actions.endPoseDrag(),
      };
    }

    if (state.mode === 'rig') {
      const joint = state.rig.selectedJoint;
      if (!joint || !state.rig.placement) return null;
      // A test pose is previewing: the skeleton on screen is not the rest skeleton, so dragging
      // a joint here would write a placement the user never actually saw. No gizmo until the
      // preview is cleared.
      if (state.rig.previewPose !== null) return null;
      boundJoint = joint;
      boundBone = null;
      return {
        key: `rig:${joint}`,
        object: proxy,
        mode: 'translate',
        space: 'world',
        onChange: onTranslateChange,
        onDragStart: () => ctx.store.actions.beginJointDrag(joint),
        onDragEnd: () => {
          ctx.store.actions.endJointDrag();
          // The moves inside the drag were debounced (they coalesce into one undo entry); the
          // end of the gesture is the point where the placement must reach IndexedDB, because
          // `beforeunload` cannot await it.
          ctx.rigTools.saveNow();
        },
      };
    }

    boundBone = null;
    boundJoint = null;
    return null;
  }

  function sync(dragging: boolean): void {
    if (dragging) return;
    const state = ctx.store.getState();

    if (state.mode === 'animate' && boundBone) {
      const bone = ctx.session.rig?.boneObject(boundBone);
      if (bone) syncProxyToBone(proxy, bone);
      return;
    }
    if (state.mode === 'rig' && boundJoint) {
      // Placement is the source of truth: the proxy follows state, state never follows the proxy
      // except through `moveJoint`. That is what makes symmetry and undo behave.
      const p = state.rig.placement?.jointWorldPositions[boundJoint];
      if (p) {
        proxy.position.set(p[0], p[1], p[2]);
        proxy.quaternion.identity();
        proxy.scale.set(1, 1, 1);
        proxy.updateMatrixWorld(true);
      }
    }
  }

  return {
    claim,
    sync,
    setAnimateMode: () => {
      /* rotate is the only legal ANIMATE mode; kept for the `R` shortcut's symmetry */
    },
    dispose() {
      overlayGroup.remove(proxy);
      boundBone = null;
      boundJoint = null;
    },
  };
}

/**
 * Bone index into a canonical `Skeleton.bones` array — the row the heat overlay reads out of
 * `RigTools.influenceMask` for the joint the user has selected. −1 when nothing is selected.
 */
export function boneIndexOfJoint(joint: CanonicalBoneId | null): number {
  return joint ? CANONICAL_BONE_IDS.indexOf(joint) : -1;
}
